import {
    ActionIcon,
    Badge,
    Button,
    Group,
    Image,
    Loader,
    Modal,
    NumberInput,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
    Textarea,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityExperienceReviewRO } from '@routellers/types/dist/city-experience-review/city-experience-review.ro';
import { ExperienceReviewStatusEnum } from '@routellers/types/dist/city-experience-review/enum/experience-review-status.enum';
import { ExperienceReviewSubjectEnum } from '@routellers/types/dist/city-experience-review/enum/experience-review-subject.enum';
import {
    AdminCapabilityEnum,
    AdminSectionEnum,
} from '@routellers/types/dist/admin/admin-permissions';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    IconCheck,
    IconLanguage,
    IconPlus,
    IconStar,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { PhotoUpload } from '../components/editor/photo-upload';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { CitySearchSelect } from '../editors/city-search-select';
import { enumLabel, enumOptions } from '../i18n';
import { useAuth } from '../providers/auth-context';
import { adminCan } from '../utils/admin-permissions';
import { slicePage } from '../utils/list';

const ALL = 'all';

const statusColor: Record<ExperienceReviewStatusEnum, string> = {
    [ExperienceReviewStatusEnum.PENDING]: 'yellow',
    [ExperienceReviewStatusEnum.APPROVED]: 'green',
    [ExperienceReviewStatusEnum.REJECTED]: 'red',
};

const stars = (rating: number) => '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating));

export const ReviewsPage = () => {
    const { t } = useTranslation();
    const { admin } = useAuth();
    const canEdit = adminCan(admin, AdminSectionEnum.REVIEWS, AdminCapabilityEnum.EDIT);
    const canDelete = adminCan(admin, AdminSectionEnum.REVIEWS, AdminCapabilityEnum.DELETE);

    const [rows, setRows] = useState<CityExperienceReviewRO[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string>(ExperienceReviewStatusEnum.PENDING);
    const [subjectFilter, setSubjectFilter] = useState<string>(ALL);
    const [selected, setSelected] = useState<CityExperienceReviewRO | null>(null);
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);

    const statusOptions = useMemo(
        () => [
            { value: ALL, label: t('pages.reviews.filters.allStatuses') },
            ...enumOptions(
                Object.values(ExperienceReviewStatusEnum),
                t,
                'enums.experienceReviewStatus',
            ),
        ],
        [t],
    );
    const subjectOptions = useMemo(
        () => [
            { value: ALL, label: t('pages.reviews.filters.allSubjects') },
            ...enumOptions(
                Object.values(ExperienceReviewSubjectEnum),
                t,
                'enums.experienceReviewSubject',
            ),
        ],
        [t],
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.cityExperienceReviewsApi.query({
                status:
                    statusFilter === ALL
                        ? undefined
                        : (statusFilter as ExperienceReviewStatusEnum),
                subjectType:
                    subjectFilter === ALL
                        ? undefined
                        : (subjectFilter as ExperienceReviewSubjectEnum),
            });
            setRows(data || []);
            setPage(1);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, subjectFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const pageRows = slicePage(rows, page);

    const moderate = async (id: number, status: ExperienceReviewStatusEnum) => {
        setBusyId(id);
        try {
            const { data } = await api.cityExperienceReviewsApi.moderate({
                id,
                data: { status },
            });
            notifications.show({
                color: 'green',
                message:
                    status === ExperienceReviewStatusEnum.APPROVED
                        ? t('pages.reviews.toasts.approved')
                        : t('pages.reviews.toasts.rejected'),
            });
            setSelected(data);
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const translateAll = async (id: number) => {
        setBusyId(id);
        try {
            const { data } = await api.cityExperienceReviewsApi.translateAll(id);
            notifications.show({
                color: 'green',
                message: t('pages.reviews.toasts.translated', {
                    count: data.locales?.length || 0,
                }),
            });
            setSelected(data);
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id: number) => {
        if (!window.confirm(t('pages.reviews.confirmDelete'))) return;
        setBusyId(id);
        try {
            await api.cityExperienceReviewsApi.remove(id);
            notifications.show({ color: 'green', message: t('pages.reviews.toasts.deleted') });
            setSelected(null);
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.reviews.title')}
                action={
                    canEdit ? (
                        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
                            {t('pages.reviews.create')}
                        </Button>
                    ) : undefined
                }
            />

            <Group gap='sm'>
                <Select
                    w={200}
                    data={statusOptions}
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v || ALL)}
                    allowDeselect={false}
                />
                <Select
                    w={200}
                    data={subjectOptions}
                    value={subjectFilter}
                    onChange={(v) => setSubjectFilter(v || ALL)}
                    allowDeselect={false}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.reviews.columns.author')}</Table.Th>
                                    <Table.Th>{t('pages.reviews.columns.subject')}</Table.Th>
                                    <Table.Th>{t('pages.reviews.columns.rating')}</Table.Th>
                                    <Table.Th>{t('pages.reviews.columns.body')}</Table.Th>
                                    <Table.Th>{t('pages.reviews.columns.locales')}</Table.Th>
                                    <Table.Th>{t('pages.reviews.columns.status')}</Table.Th>
                                    <Table.Th w={140} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelected(row)}
                                    >
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.authorAvatarUrl}
                                                name={row.authorName}
                                                subtitle={new Date(row.createdAt).toLocaleDateString()}
                                                radius='xl'
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size='sm' fw={500}>
                                                {enumLabel(
                                                    t,
                                                    'enums.experienceReviewSubject',
                                                    row.subjectType,
                                                )}
                                            </Text>
                                            <Text size='xs' c='dimmed' lineClamp={1}>
                                                {row.subjectTitle || `#${row.cityQuestId || row.cityReadyRouteId}`}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size='sm' c='yellow.7'>
                                                {stars(row.rating)}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td maw={280}>
                                            <Text size='sm' lineClamp={2}>
                                                {row.body}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size='sm'>
                                                {(row.locales || [])
                                                    .map((l) => l.language.toUpperCase())
                                                    .join(', ') || '—'}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={statusColor[row.status]} variant='light'>
                                                {enumLabel(
                                                    t,
                                                    'enums.experienceReviewStatus',
                                                    row.status,
                                                )}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td onClick={(e) => e.stopPropagation()}>
                                            <Group gap={4} wrap='nowrap'>
                                                {canEdit &&
                                                row.status !== ExperienceReviewStatusEnum.APPROVED ? (
                                                    <Tooltip label={t('pages.reviews.actions.approve')}>
                                                        <ActionIcon
                                                            color='green'
                                                            variant='light'
                                                            loading={busyId === row.id}
                                                            onClick={() =>
                                                                moderate(
                                                                    row.id,
                                                                    ExperienceReviewStatusEnum.APPROVED,
                                                                )
                                                            }
                                                        >
                                                            <IconCheck size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : null}
                                                {canEdit &&
                                                row.status !== ExperienceReviewStatusEnum.REJECTED ? (
                                                    <Tooltip label={t('pages.reviews.actions.reject')}>
                                                        <ActionIcon
                                                            color='red'
                                                            variant='light'
                                                            loading={busyId === row.id}
                                                            onClick={() =>
                                                                moderate(
                                                                    row.id,
                                                                    ExperienceReviewStatusEnum.REJECTED,
                                                                )
                                                            }
                                                        >
                                                            <IconX size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : null}
                                                {canEdit ? (
                                                    <Tooltip
                                                        label={t('pages.reviews.actions.translateAll')}
                                                    >
                                                        <ActionIcon
                                                            color='blue'
                                                            variant='light'
                                                            loading={busyId === row.id}
                                                            onClick={() => translateAll(row.id)}
                                                        >
                                                            <IconLanguage size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : null}
                                                {canDelete ? (
                                                    <Tooltip label={t('pages.reviews.actions.delete')}>
                                                        <ActionIcon
                                                            color='gray'
                                                            variant='light'
                                                            loading={busyId === row.id}
                                                            onClick={() => remove(row.id)}
                                                        >
                                                            <IconTrash size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : null}
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.reviews.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={rows.length} onChange={setPage} />
                </>
            )}

            <ReviewDetailModal
                review={selected}
                busy={busyId === selected?.id}
                canEdit={canEdit}
                canDelete={canDelete}
                onClose={() => setSelected(null)}
                onApprove={() =>
                    selected && moderate(selected.id, ExperienceReviewStatusEnum.APPROVED)
                }
                onReject={() =>
                    selected && moderate(selected.id, ExperienceReviewStatusEnum.REJECTED)
                }
                onTranslate={() => selected && translateAll(selected.id)}
                onDelete={() => selected && remove(selected.id)}
            />

            <CreateReviewModal
                opened={creating}
                onClose={() => setCreating(false)}
                onCreated={async () => {
                    setCreating(false);
                    await load();
                }}
            />
        </Stack>
    );
};

function ReviewDetailModal({
    review,
    busy,
    canEdit,
    canDelete,
    onClose,
    onApprove,
    onReject,
    onTranslate,
    onDelete,
}: {
    review: CityExperienceReviewRO | null;
    busy: boolean;
    canEdit: boolean;
    canDelete: boolean;
    onClose: () => void;
    onApprove: () => void;
    onReject: () => void;
    onTranslate: () => void;
    onDelete: () => void;
}) {
    const { t } = useTranslation();

    return (
        <Modal
            opened={review != null}
            onClose={onClose}
            title={t('pages.reviews.detailTitle')}
            size='lg'
            centered
        >
            {review ? (
                <Stack gap='md'>
                    <Group justify='space-between' align='flex-start'>
                        <EntityThumb
                            src={review.authorAvatarUrl}
                            name={review.authorName}
                            subtitle={new Date(review.createdAt).toLocaleString()}
                            radius='xl'
                        />
                        <Badge color={statusColor[review.status]} variant='light'>
                            {enumLabel(t, 'enums.experienceReviewStatus', review.status)}
                        </Badge>
                    </Group>

                    <div>
                        <Text size='sm' c='dimmed'>
                            {enumLabel(t, 'enums.experienceReviewSubject', review.subjectType)} ·{' '}
                            {review.subjectTitle || '—'}
                        </Text>
                        <Text c='yellow.7' mt={4}>
                            {stars(review.rating)}
                        </Text>
                    </div>

                    <Text style={{ whiteSpace: 'pre-wrap' }}>{review.body}</Text>

                    {review.photoUrls?.length ? (
                        <SimpleGrid cols={3} spacing='sm'>
                            {review.photoUrls.map((url) => (
                                <Image key={url} src={url} radius='md' h={100} fit='cover' />
                            ))}
                        </SimpleGrid>
                    ) : null}

                    <div>
                        <Text size='sm' fw={600} mb={4}>
                            {t('pages.reviews.localesHeading')}
                        </Text>
                        <Stack gap={6}>
                            {(review.locales || []).map((locale) => (
                                <div key={locale.language}>
                                    <Text size='xs' c='dimmed' tt='uppercase'>
                                        {locale.language}
                                    </Text>
                                    <Text size='sm' lineClamp={3}>
                                        {locale.body}
                                    </Text>
                                </div>
                            ))}
                            {!review.locales?.length ? (
                                <Text size='sm' c='dimmed'>
                                    {t('pages.reviews.noLocales')}
                                </Text>
                            ) : null}
                        </Stack>
                    </div>

                    <Group justify='space-between'>
                        <Group>
                            {canEdit &&
                            review.status !== ExperienceReviewStatusEnum.APPROVED ? (
                                <Button
                                    color='green'
                                    leftSection={<IconCheck size={16} />}
                                    loading={busy}
                                    onClick={onApprove}
                                >
                                    {t('pages.reviews.actions.approve')}
                                </Button>
                            ) : null}
                            {canEdit &&
                            review.status !== ExperienceReviewStatusEnum.REJECTED ? (
                                <Button
                                    color='red'
                                    variant='light'
                                    leftSection={<IconX size={16} />}
                                    loading={busy}
                                    onClick={onReject}
                                >
                                    {t('pages.reviews.actions.reject')}
                                </Button>
                            ) : null}
                            {canEdit ? (
                                <Button
                                    variant='light'
                                    leftSection={<IconLanguage size={16} />}
                                    loading={busy}
                                    onClick={onTranslate}
                                >
                                    {t('pages.reviews.actions.translateAll')}
                                </Button>
                            ) : null}
                        </Group>
                        {canDelete ? (
                            <Button
                                color='gray'
                                variant='subtle'
                                leftSection={<IconTrash size={16} />}
                                loading={busy}
                                onClick={onDelete}
                            >
                                {t('pages.reviews.actions.delete')}
                            </Button>
                        ) : null}
                    </Group>
                </Stack>
            ) : null}
        </Modal>
    );
}

function CreateReviewModal({
    opened,
    onClose,
    onCreated,
}: {
    opened: boolean;
    onClose: () => void;
    onCreated: () => Promise<void>;
}) {
    const { t } = useTranslation();
    const languageOptions = useMemo(
        () => enumOptions(Object.values(Languages), t, 'enums.contentLanguage'),
        [t],
    );
    const subjectOptions = useMemo(
        () =>
            enumOptions(Object.values(ExperienceReviewSubjectEnum), t, 'enums.experienceReviewSubject'),
        [t],
    );

    const [cityId, setCityId] = useState<number | null>(null);
    const [subjectType, setSubjectType] = useState<ExperienceReviewSubjectEnum>(
        ExperienceReviewSubjectEnum.ROUTE,
    );
    const [subjectId, setSubjectId] = useState<string | null>(null);
    const [subjectOptionsList, setSubjectOptionsList] = useState<
        Array<{ value: string; label: string }>
    >([]);
    const [authorName, setAuthorName] = useState('');
    const [authorAvatarUrl, setAuthorAvatarUrl] = useState<string | null>(null);
    const [sourceLanguage, setSourceLanguage] = useState<Languages>(Languages.EN);
    const [rating, setRating] = useState<number | string>(5);
    const [body, setBody] = useState('');
    const [photoUrls, setPhotoUrls] = useState<string[]>([]);
    const [publish, setPublish] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);

    useEffect(() => {
        if (!opened) return;
        setCityId(null);
        setSubjectType(ExperienceReviewSubjectEnum.ROUTE);
        setSubjectId(null);
        setSubjectOptionsList([]);
        setAuthorName('');
        setAuthorAvatarUrl(null);
        setSourceLanguage(Languages.EN);
        setRating(5);
        setBody('');
        setPhotoUrls([]);
        setPublish(true);
    }, [opened]);

    useEffect(() => {
        if (!cityId) {
            setSubjectOptionsList([]);
            setSubjectId(null);
            return;
        }

        let cancelled = false;
        const loadSubjects = async () => {
            setLoadingSubjects(true);
            try {
                if (subjectType === ExperienceReviewSubjectEnum.QUEST) {
                    const { data } = await api.cityQuestsApi.query(undefined);
                    if (cancelled) return;
                    setSubjectOptionsList(
                        (data || [])
                            .filter((q) => q.cityId === cityId)
                            .map((q) => ({
                                value: String(q.id),
                                label: q.title || q.key || `#${q.id}`,
                            })),
                    );
                } else {
                    const { data } = await api.cityReadyRoutesApi.query({
                        page: 1,
                        limit: 100,
                        where: { cityId },
                    });
                    if (cancelled) return;
                    setSubjectOptionsList(
                        (data?.items || []).map((r) => ({
                            value: String(r.id),
                            label: r.title || `#${r.id}`,
                        })),
                    );
                }
                setSubjectId(null);
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    setSubjectOptionsList([]);
                }
            } finally {
                if (!cancelled) setLoadingSubjects(false);
            }
        };

        loadSubjects();
        return () => {
            cancelled = true;
        };
    }, [cityId, subjectType]);

    const create = async () => {
        if (!cityId || !subjectId || !authorName.trim() || body.trim().length < 20) {
            notifications.show({
                color: 'red',
                message: t('pages.reviews.createValidation'),
            });
            return;
        }

        setSaving(true);
        try {
            await api.cityExperienceReviewsApi.create({
                data: {
                    subjectType,
                    cityId,
                    cityQuestId:
                        subjectType === ExperienceReviewSubjectEnum.QUEST
                            ? Number(subjectId)
                            : undefined,
                    cityReadyRouteId:
                        subjectType === ExperienceReviewSubjectEnum.ROUTE
                            ? Number(subjectId)
                            : undefined,
                    authorName: authorName.trim(),
                    authorAvatarUrl,
                    sourceLanguage,
                    rating: Number(rating) || 5,
                    body: body.trim(),
                    photoUrls,
                    verifiedCompletion: true,
                    publish,
                },
            });
            notifications.show({ color: 'green', message: t('pages.reviews.toasts.created') });
            await onCreated();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('pages.reviews.create')}
            size='lg'
            centered
        >
            <Stack gap='md'>
                <CitySearchSelect value={cityId} onChange={setCityId} />
                <Select
                    label={t('pages.reviews.fields.subjectType')}
                    data={subjectOptions}
                    value={subjectType}
                    onChange={(v) => v && setSubjectType(v as ExperienceReviewSubjectEnum)}
                    allowDeselect={false}
                />
                <Select
                    label={t('pages.reviews.fields.subject')}
                    data={subjectOptionsList}
                    value={subjectId}
                    onChange={(v) => setSubjectId(v)}
                    searchable
                    disabled={!cityId}
                    rightSection={loadingSubjects ? <Loader size={14} /> : undefined}
                    placeholder={
                        cityId
                            ? t('pages.reviews.fields.subjectPlaceholder')
                            : t('pages.reviews.fields.pickCityFirst')
                    }
                />
                <TextInput
                    label={t('pages.reviews.fields.authorName')}
                    required
                    value={authorName}
                    onChange={(e) => setAuthorName(e.currentTarget.value)}
                />
                <PhotoUpload
                    variant='avatar'
                    label={t('pages.reviews.fields.authorAvatar')}
                    value={authorAvatarUrl}
                    onChange={setAuthorAvatarUrl}
                    imageType={ImageTypeEnum.CITY_EXPERIENCE_REVIEW}
                />
                <Group grow>
                    <Select
                        label={t('pages.reviews.fields.sourceLanguage')}
                        data={languageOptions}
                        value={sourceLanguage}
                        onChange={(v) => v && setSourceLanguage(v as Languages)}
                        allowDeselect={false}
                    />
                    <NumberInput
                        label={t('pages.reviews.fields.rating')}
                        min={1}
                        max={5}
                        value={rating}
                        onChange={(v) => setRating(typeof v === 'number' ? v : Number(v) || 5)}
                        leftSection={<IconStar size={14} />}
                    />
                </Group>
                <Textarea
                    label={t('pages.reviews.fields.body')}
                    description={t('pages.reviews.fields.bodyHint')}
                    minRows={4}
                    autosize
                    value={body}
                    onChange={(e) => setBody(e.currentTarget.value)}
                    required
                />
                <PhotoUpload
                    variant='gallery'
                    label={t('pages.reviews.fields.photos')}
                    value={photoUrls}
                    onChange={setPhotoUrls}
                    imageType={ImageTypeEnum.CITY_EXPERIENCE_REVIEW}
                    max={3}
                />
                <Select
                    label={t('pages.reviews.fields.publish')}
                    data={[
                        { value: 'yes', label: t('pages.reviews.fields.publishYes') },
                        { value: 'no', label: t('pages.reviews.fields.publishNo') },
                    ]}
                    value={publish ? 'yes' : 'no'}
                    onChange={(v) => setPublish(v === 'yes')}
                    allowDeselect={false}
                />
                <Group justify='flex-end'>
                    <Button variant='default' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} onClick={create}>
                        {t('pages.reviews.create')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
